import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
import { CompositeScoreWidgetComponent } from '../../components/composite-score-widget/composite-score-widget.component';
import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';

@Component({
  selector: 'app-generic-prompts',
  standalone: true,
  imports: [CommonModule, MatAngularModule, CompositeScoreWidgetComponent, BarLineComboChartComponent],
  templateUrl: './generic-prompts.component.html',
  styleUrl: './generic-prompts.component.scss',
})
export class GenericPromptsComponent implements OnInit, OnDestroy {
  promptsData: any = null;
  private subscriptions: Subscription[] = [];
  view: [number, number] = [900, 360];

  constructor(private templateCommonService: GenericTemplateCommonService) {}

  ngOnInit(): void {
    const sub = this.templateCommonService.analysisResponse$.subscribe((response: any) => {
      if (response) {
        this.promptsData = response?.prompts ?? response?.structured_data?.prompts ?? null;
      }
    });
    this.subscriptions.push(sub);
    const current = this.templateCommonService.getCurrentAnalysisResponse();
    if (current) {
      this.promptsData = current?.prompts ?? current?.structured_data?.prompts ?? null;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  get totalGenericPrompts(): number {
    return Number(this.promptsData?.summary?.total_generic_prompts ?? 0);
  }

  get genericRecommendedPct(): number {
    return Number(this.promptsData?.summary?.primary_brand_recommended_percent_generic ?? 0);
  }

  get brandedRecommendedPct(): number {
    return Number(this.promptsData?.summary?.primary_brand_recommended_percent_branded ?? 0);
  }

  get chartCategories(): string[] {
    return Array.isArray(this.promptsData?.chart?.categories) ? this.promptsData.chart.categories : [];
  }

  get chartSeries(): any[] {
    return Array.isArray(this.promptsData?.chart?.series) ? this.promptsData.chart.series : [];
  }

  get groupedChartData(): any[] {
    const categories = this.chartCategories;
    const series = this.chartSeries;
    if (!categories.length || !series.length) return [];
    return categories.map((cat: string, idx: number) => ({
      name: cat,
      series: series.map((b: any) => ({
        name: b?.name ?? 'Brand',
        value: Number(Array.isArray(b?.values) ? b.values[idx] : 0),
      })),
    }));
  }

  get barBrandName(): string {
    return this.chartSeries?.[0]?.name ?? 'Your Brand';
  }

  get chartResults(): any[] {
    const categories = this.chartCategories;
    const first = this.chartSeries?.[0];
    if (!categories.length || !first) return [];
    const vals = Array.isArray(first.values) ? first.values : [];
    return categories.map((name: string, idx: number) => ({ name, value: Number(vals[idx] ?? 0) }));
  }

  get chartLineSeries(): any[] {
    const categories = this.chartCategories;
    if (!categories.length || this.chartSeries.length <= 1) return [];
    return this.chartSeries.slice(1).map((s: any) => {
      const vals = Array.isArray(s?.values) ? s.values : [];
      return {
        name: s?.name ?? 'Competitor',
        series: categories.map((name: string, idx: number) => ({ name, value: Number(vals[idx] ?? 0) })),
      };
    });
  }

  get tableRows(): any[] {
    return Array.isArray(this.promptsData?.table?.rows) ? this.promptsData.table.rows : [];
  }

  get tableColumns(): string[] {
    const cols = this.promptsData?.table?.columns;
    return Array.isArray(cols) ? cols : [];
  }

  getGrowingPromptCategories(): string[] {
    const gp = this.promptsData?.growing_prompts;
    if (!gp || typeof gp !== 'object') return [];
    return Object.keys(gp).filter((k) => Array.isArray(gp[k]) && gp[k].length > 0);
  }

  getGrowingPromptsForCategory(category: string): any[] {
    const rows = this.promptsData?.growing_prompts?.[category];
    return Array.isArray(rows) ? rows : [];
  }

  getPromptTypeDisplay(row: any): string {
    return String(row?.prompt_type || 'Informational');
  }

  getColumnLabel(col: string): string {
    const map: Record<string, string> = {
      opinion_based: 'Opinion-based',
      prompt_type: 'Type of prompts',
      ai_volume: 'AI Volume',
    };
    if (map[col]) return map[col];
    return String(col || '').replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
  }

  formatPercentCell(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'number' && Number.isFinite(value)) return `${Math.round(value)}%`;
    const text = String(value).trim();
    const numeric = Number(text.replace('%', '').trim());
    if (Number.isFinite(numeric)) return `${Math.round(numeric)}%`;
    return text;
  }
}
